/* Klientské formuláře ve složce klienta — strana poradce.
 *
 * CO TO DĚLÁ: poradce vybere TYP formuláře, jazyk a platnost, vyrobí odkaz
 * a dá si do schránky hotovou zprávu. Vidí stav (čeká / rozpracováno / vyplněno),
 * může odkaz zneplatnit a vyplněné odpovědi si otevřít.
 *
 * CO TO NEDĚLÁ A DĚLAT NEBUDE: neodesílá. Žádný e-mail, žádná SMS, žádný
 * WhatsApp přes server. Odkaz vede na formulář, který se u zdravotního typu
 * ptá na zdravotní stav — kdyby ho rozesílala aplikace, jedna špatně uložená
 * adresa u klienta by poslala takovou výzvu cizímu člověku, a to jménem
 * poradce. Zpráva se proto jen připraví a poradce ji odešle svým kanálem,
 * kde vidí, komu ji posílá.
 *
 * ZDRAVOTNÍ DOTAZNÍK SE V NÁHLEDU NEZOBRAZUJE. Nemá „náhled“ ani maskovaný —
 * server ho v detailu nevrací vůbec. Poradce si ho otevře tlačítkem, což je
 * vědomý krok a zapíše se do auditu. Není to buzerace: mít zdravotní údaje
 * klienta na obrazovce pokaždé, když se otevře jeho složka, je přesně ten
 * stav, kterému se celý modul vyhýbá.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, Ban, Copy, Eye, EyeOff, Heart, Link2, Loader2, Lock, RefreshCw, Shield, Wallet,
} from "lucide-react";
import { useLanguage, LANGUAGES } from "@/contexts/LanguageContext";
import {
  nactiDetail, nactiFormulare, nactiPlne, nactiTypy, odhalPole, vytvorOdkaz, zneplatniOdkaz,
} from "./formularApi";
import { relevantniPoleSekce, sekceRelevantni, jeVyplneno } from "@/lib/formulare/podminky";

import { TL } from "@/lib/prekladyUI";
/* Nabídka platnosti. Sedm dní je výchozí u zdravotního dotazníku: odkaz, který
   se ptá na zdravotní stav, nemá viset na internetu měsíc. Strop na typ hlídá
   server (lib/formulare/jadro.py) — tohle je jen nabídka. */
const PLATNOSTI = [7, 14, 30];

const IKONY = { heart: Heart, shield: Shield, wallet: Wallet };

async function zkopiruj(text) {
  try {
    if (!navigator?.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    return false;
  }
}

/** Odkaz se skládá z cesty, kterou vrátil SERVER — v ní je token. Frontend si
 *  adresu nikdy nesestaví z id klienta; kdyby ano, stačilo by uhodnout id. */
function plnaUrl(urlPath) {
  if (!urlPath) return "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}${urlPath}`;
}

function stavPolozky(p, t) {
  if (p.souhlas_odvolan) return { text: t("formKlient.poradce.stavOdvolano"), barva: "#C53030" };
  if (p.odeslano_klientem) return { text: t("formKlient.poradce.stavVyplneno"), barva: "var(--positive, #2F855A)" };
  if (p.zneplatneno) return { text: t("formKlient.poradce.stavZneplatneno"), barva: "var(--muted, #6b6355)" };
  if (!p.url_path) return { text: t("formKlient.poradce.stavVyprselo"), barva: "var(--muted, #6b6355)" };
  if (p.vyplnenych_poli > 0) return { text: t("formKlient.poradce.stavRozpracovano"), barva: "var(--accent, #F47920)" };
  return { text: t("formKlient.poradce.stavCeka"), barva: "var(--muted, #6b6355)" };
}

/* ── čtení odpovědí ────────────────────────────────────────────────────── */

function Odpovedi({ polozka, jazyk, t }) {
  const [stav, setStav] = useState("cekam");   // cekam|nacitam|ok|chyba
  const [data, setData] = useState(null);
  const [odhalene, setOdhalene] = useState({});

  const nacti = async () => {
    setStav("nacitam");
    try {
      /* U zvláštní kategorie je /plny JEDINÁ cesta k obsahu a zapíše se do
         auditu. U běžného formuláře stačí detail s maskovanými poli — plnou
         hodnotu si poradce odemkne po jednom, taky se záznamem. */
      const d = polozka.zvlastni_kategorie
        ? await nactiPlne(polozka.id, jazyk)
        : await nactiDetail(polozka.id, jazyk);
      setData(d);
      setStav("ok");
    } catch (e) {
      setStav("chyba");
    }
  };

  if (stav === "cekam") {
    return (
      <button type="button" onClick={nacti} data-testid={`kf-zobrazit-${polozka.id}`}
        className="inline-flex items-center gap-1.5 text-maly font-semibold py-2 px-3 rounded-full"
        style={{ border: "1px solid var(--line)", color: "var(--ink)" }}>
        <Eye size={13} aria-hidden="true" /> {t("formKlient.poradce.zobrazit")}
      </button>
    );
  }
  if (stav === "nacitam") {
    return <Loader2 className="animate-spin" size={16} style={{ color: "var(--accent)" }} />;
  }
  if (stav === "chyba") {
    return (
      <p className="text-maly" data-testid={`kf-chyba-odpovedi-${polozka.id}`} style={{ color: "var(--negative-text)" }}>
        {t("formKlient.poradce.chybaOdpovedi")}
      </p>
    );
  }

  const odpovedi = data?.odpovedi || {};
  const sekce = (data?.sekce || []).filter((s) => sekceRelevantni(s, odpovedi));

  return (
    <div className="grid gap-3" data-testid={`kf-odpovedi-${polozka.id}`}>
      <button type="button" onClick={() => { setStav("cekam"); setData(null); setOdhalene({}); }}
        className="justify-self-start inline-flex items-center gap-1.5 text-maly py-1"
        style={{ color: "var(--muted)" }}>
        <EyeOff size={13} aria-hidden="true" /> {t("formKlient.poradce.skryt")}
      </button>
      {sekce.map((s) => {
        const pole = relevantniPoleSekce(s, odpovedi).filter((pl) => jeVyplneno(odpovedi[pl.id]));
        if (!pole.length) return null;
        return (
          <div key={s.id} className="grid gap-1.5">
            <h5 className="text-drobny font-bold uppercase tracking-wide" style={{ color: "var(--muted)" }}>
              {s.nazev}
            </h5>
            {pole.map((pl) => (
              <div key={pl.id} className="grid grid-cols-[1fr_auto] gap-2 items-start text-[13px]">
                <span style={{ color: "var(--muted)" }}>{pl.label}</span>
                <span className="text-right font-medium" style={{ color: "var(--ink)" }}>
                  {pl.typ === "tabulka" && Array.isArray(odpovedi[pl.id]) ? (
                    <span className="grid">
                      {odpovedi[pl.id].map((r, i) => (
                        <span key={i}>
                          {(pl.sloupce || []).map((c) => r?.[c.id]).filter(Boolean).join(" · ")}
                        </span>
                      ))}
                    </span>
                  ) : pl.citlive && !data.zvlastni_kategorie ? (
                    <span className="inline-flex items-center gap-2">
                      {odhalene[pl.id] || odpovedi[pl.id]}
                      {!odhalene[pl.id] && (
                        <button type="button" data-testid={`kf-odhalit-${pl.id}`}
                          onClick={async () => {
                            try {
                              const r = await odhalPole(polozka.id, pl.id);
                              setOdhalene((o) => ({ ...o, [pl.id]: r.hodnota }));
                            } catch (e) {
                              toast.error(t("formKlient.poradce.chybaOdpovedi"));
                            }
                          }}
                          className="text-drobny" style={{ color: "var(--accent)" }}>
                          {t("formKlient.poradce.odhalit")}
                        </button>
                      )}
                    </span>
                  ) : (
                    String(odpovedi[pl.id] ?? "")
                  )}
                </span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/* ── karta ─────────────────────────────────────────────────────────────── */

export default function KlientskeFormularKarta({ contact }) {
  const { t, lang, currency } = useLanguage();
  const contactId = contact?.id;

  const [typy, setTypy] = useState([]);
  const [polozky, setPolozky] = useState(null);       // null = ještě nenačteno
  const [chyba, setChyba] = useState("");
  const [zvolenyTyp, setZvolenyTyp] = useState("");
  const [jazykOdkazu, setJazykOdkazu] = useState(lang);
  const [platnost, setPlatnost] = useState(PLATNOSTI[0]);
  const [tvorim, setTvorim] = useState(false);
  const [posledni, setPosledni] = useState(null);     // { url, zprava }

  const nacti = useCallback(async () => {
    if (!contactId) return;
    setChyba("");
    try {
      const data = await nactiFormulare(contactId);
      setPolozky(data);
    } catch (e) {
      /* Chyba načtení NENÍ prázdný stav. Kdyby se ukázalo „nic neposláno“,
         poradce by poslal druhý odkaz na formulář, který klient možná
         vyplňuje — a klient by vyplňoval znovu od začátku. */
      setPolozky(null);
      setChyba(t("formKlient.poradce.chybaNacteni"));
    }
  }, [contactId, t]);

  useEffect(() => { nacti(); }, [nacti]);

  useEffect(() => {
    let zruseno = false;
    nactiTypy(lang)
      .then((d) => {
        if (zruseno) return;
        setTypy(d);
        setZvolenyTyp((s) => s || d[0]?.id || "");
      })
      .catch(() => { if (!zruseno) setTypy([]); });
    return () => { zruseno = true; };
  }, [lang]);

  const typ = useMemo(() => typy.find((x) => x.id === zvolenyTyp) || null, [typy, zvolenyTyp]);

  /* Nabídka platnosti se ořízne stropem typu. Zdravotní dotazník má strop 30
     dní a nabízet 60 by znamenalo, že poradce klikne na volbu, kterou mu
     server tiše sníží — a on si bude myslet, že odkaz platí dvakrát dýl. */
  const platnosti = useMemo(() => {
    const strop = typ?.platnost_max_dnu || 60;
    return PLATNOSTI.filter((d) => d <= strop);
  }, [typ]);

  useEffect(() => {
    if (!platnosti.includes(platnost)) setPlatnost(platnosti[0] || PLATNOSTI[0]);
  }, [platnosti, platnost]);

  const vytvor = async () => {
    if (!contactId || !zvolenyTyp || tvorim) return;
    setTvorim(true);
    try {
      const r = await vytvorOdkaz({
        contactId, typ: zvolenyTyp, platnostDnu: platnost, jazyk: jazykOdkazu,
      });
      const url = plnaUrl(r.url_path);
      /* Zprávu složil SERVER v jazyce klienta a nechal v ní `{odkaz}` —
         adresu, na které appka běží, zná jistě jen prohlížeč. */
      const zprava = String(r.zprava || "").replace("{odkaz}", url);
      setPosledni({ url, zprava });
      await nacti();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("formKlient.poradce.chybaVytvoreni"));
    } finally {
      setTvorim(false);
    }
  };

  const zneplatni = async (id) => {
    // eslint-disable-next-line no-alert
    if (typeof window !== "undefined" && !window.confirm(t("formKlient.poradce.zneplatnitPotvrzeni"))) return;
    try {
      await zneplatniOdkaz(id);
      setPosledni(null);
      await nacti();
    } catch (e) {
      toast.error(t("formKlient.poradce.chybaNacteni"));
    }
  };

  const kopiruj = async (text, hlaska) => {
    const ok = await zkopiruj(text);
    if (ok) toast.success(hlaska);
    else toast.error(text);   // schránka nedostupná → ať to jde aspoň vybrat myší
  };

  return (
    <section className="grid gap-4" data-testid="klientske-formulare">
      <header className="grid gap-1">
        <h3 className="text-[15px] font-bold" style={{ color: "var(--ink)" }}>
          {t("formKlient.poradce.nadpis")}
        </h3>
        <p className="text-maly" style={{ color: "var(--muted)" }}>
          {t("formKlient.poradce.popis")}
        </p>
      </header>

      {/* ── výběr typu ────────────────────────────────────────────────── */}
      <div className="grid gap-2.5 p-4"
        style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 14 }}>
        <div className="grid gap-2 sm:grid-cols-2">
          {typy.map((x) => {
            const Ikona = IKONY[x.ikona] || Link2;
            const vybrano = x.id === zvolenyTyp;
            return (
              <button key={x.id} type="button" onClick={() => setZvolenyTyp(x.id)}
                data-testid={`kf-typ-${x.id}`}
                className="text-left p-3 grid gap-1"
                style={{
                  borderRadius: 12,
                  border: `1px solid ${vybrano ? "var(--accent)" : "var(--line)"}`,
                  background: vybrano ? "var(--surface-2)" : "transparent",
                }}>
                <span className="inline-flex items-center gap-2 text-text font-bold"
                  style={{ color: "var(--ink)" }}>
                  <Ikona size={14} aria-hidden="true" /> {x.nazev}
                </span>
                <span className="text-drobny" style={{ color: "var(--muted)" }}>{x.podnadpis}</span>
                {x.zvlastni_kategorie && (
                  <span className="inline-flex items-center gap-1 text-drobny font-semibold"
                    data-testid={`kf-zvlastni-${x.id}`} style={{ color: "var(--negative-text)" }}>
                    <Lock size={10} aria-hidden="true" /> {t("formKlient.poradce.zvlastniKategorie")}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {typ?.zvlastni_kategorie && (
          <p className="text-drobny leading-relaxed p-2.5" data-testid="kf-zvlastni-hint"
            style={{ background: "var(--surface-2)", borderRadius: 10, color: "var(--muted)" }}>
            {t("formKlient.poradce.zvlastniHint")}
          </p>
        )}

        <div className="grid gap-2 sm:grid-cols-2">
          <label className="grid gap-1">
            <span className="text-drobny" style={{ color: "var(--muted)" }}>
              {t("formKlient.poradce.jazykOdkazu")}
            </span>
            <select value={jazykOdkazu} onChange={(e) => setJazykOdkazu(e.target.value)}
              data-testid="kf-jazyk"
              className="text-[13px] px-2.5 py-2 rounded-lg"
              style={{ background: "var(--surface)", border: "1px solid var(--line)", color: "var(--ink)" }}>
              {LANGUAGES.map((j) => <option key={j.code} value={j.code}>{j.name}</option>)}
            </select>
          </label>
          <label className="grid gap-1">
            <span className="text-drobny" style={{ color: "var(--muted)" }}>
              {t("formKlient.poradce.platnost")}
            </span>
            <select value={platnost} onChange={(e) => setPlatnost(Number(e.target.value))}
              data-testid="kf-platnost"
              className="text-[13px] px-2.5 py-2 rounded-lg"
              style={{ background: "var(--surface)", border: "1px solid var(--line)", color: "var(--ink)" }}>
              {platnosti.map((d) => (
                <option key={d} value={d}>{t("formKlient.poradce.dnu", { n: d })}</option>
              ))}
            </select>
          </label>
        </div>

        <button type="button" onClick={vytvor} disabled={!zvolenyTyp || tvorim}
          data-testid="kf-vytvorit"
          className="inline-flex items-center justify-center gap-2 text-[13px] font-bold py-2.5 rounded-full"
          style={{
            background: zvolenyTyp && !tvorim ? "var(--accent)" : "var(--surface-2)",
            color: zvolenyTyp && !tvorim ? "var(--on-accent)" : "var(--muted)",
          }}>
          <Link2 size={14} aria-hidden="true" />
          {tvorim ? t("formKlient.poradce.vytvarim") : t("formKlient.poradce.vytvorit")}
        </button>

        <p className="text-drobny" style={{ color: "var(--muted)" }}>
          {t("formKlient.poradce.nicNeodesilame")}
        </p>
      </div>

      {/* ── čerstvý odkaz + zpráva ───────────────────────────────────── */}
      {posledni && (
        <div className="grid gap-2 p-4" data-testid="kf-novy-odkaz"
          style={{ background: "var(--surface)", border: "1px solid var(--accent)", borderRadius: 14 }}>
          <code className="text-drobny break-all" style={{ color: "var(--muted)" }}>{posledni.url}</code>
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid="kf-kopirovat-odkaz"
              onClick={() => kopiruj(posledni.url, t("formKlient.poradce.zkopirovano"))}
              className="inline-flex items-center gap-1.5 text-maly font-semibold py-2 px-3 rounded-full"
              style={{ border: "1px solid var(--line)", color: "var(--ink)" }}>
              <Copy size={13} aria-hidden="true" /> {t("formKlient.poradce.zkopirovat")}
            </button>
            <button type="button" data-testid="kf-kopirovat-zpravu"
              onClick={() => kopiruj(posledni.zprava, t("formKlient.poradce.zkopirovano"))}
              className="inline-flex items-center gap-1.5 text-maly font-semibold py-2 px-3 rounded-full"
              style={{ background: "var(--accent)", color: "var(--on-accent)" }}>
              <Copy size={13} aria-hidden="true" /> {t("formKlient.poradce.zkopirovatZpravu")}
            </button>
          </div>
          <pre className="text-[12px] whitespace-pre-wrap p-3 m-0"
            style={{ background: "var(--surface-2)", borderRadius: 10, color: "var(--ink)" }}>
            {posledni.zprava}
          </pre>
        </div>
      )}

      {/* ── seznam ───────────────────────────────────────────────────── */}
      {chyba && (
        <div className="flex items-center justify-between gap-3 p-3" data-testid="kf-chyba"
          style={{ background: "var(--surface)", border: "1px solid #C53030", borderRadius: 12 }}>
          <span className="inline-flex items-center gap-2 text-maly" style={{ color: "var(--negative-text)" }}>
            <AlertTriangle size={14} aria-hidden="true" /> {chyba}
          </span>
          <button aria-label={TL("Načíst znovu")} type="button" onClick={nacti} data-testid="kf-znovu"
            className="inline-flex items-center gap-1.5 text-maly" style={{ color: "var(--ink)" }}>
            <RefreshCw size={13} aria-hidden="true" />
          </button>
        </div>
      )}

      {polozky && polozky.length === 0 && !chyba && (
        <p className="text-maly" data-testid="kf-prazdno" style={{ color: "var(--muted)" }}>
          {t("formKlient.poradce.bezFormularu")}
        </p>
      )}

      {(polozky || []).map((p) => {
        const s = stavPolozky(p, t);
        return (
          <article key={p.id} className="grid gap-2 p-4" data-testid={`kf-polozka-${p.id}`}
            style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 14 }}>
            <div className="flex items-start justify-between gap-3">
              <div className="grid gap-0.5">
                <span className="text-text font-bold" style={{ color: "var(--ink)" }}>{p.nazev}</span>
                <span className="text-drobny" style={{ color: "var(--muted)" }}>
                  {p.plati_do ? t("formKlient.poradce.platiDo", {
                    datum: new Date(p.plati_do).toLocaleDateString(currency.locale),
                  }) : ""}
                </span>
              </div>
              <span className="text-drobny font-bold px-2.5 py-1 rounded-full shrink-0"
                data-testid={`kf-stav-${p.id}`}
                style={{ color: s.barva, border: `1px solid ${s.barva}`, background: "var(--surface-2)" }}>
                {s.text}
              </span>
            </div>

            {p.celkem_poli > 0 && (
              <p className="text-drobny" style={{ color: "var(--muted)" }}>
                {t("formKlient.poradce.vyplneno", { n: p.vyplnenych_poli || 0, celkem: p.celkem_poli })}
              </p>
            )}

            {p.prevzato_do_analyzy && (
              <p className="text-drobny" data-testid={`kf-prevzato-${p.id}`} style={{ color: "var(--positive-text)" }}>
                {t("formKlient.poradce.pv.prevzato")} · {new Date(p.prevzato_do_analyzy).toLocaleDateString(currency.locale)}
              </p>
            )}

            {p.k_uklidu && (
              <p className="text-drobny inline-flex items-start gap-1.5" data-testid={`kf-uklid-${p.id}`}
                style={{ color: "var(--negative-text)" }}>
                <AlertTriangle size={12} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
                {t("formKlient.poradce.kUklidu", { n: p.retence_dnu })}
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              {p.url_path && (
                <button type="button" data-testid={`kf-kopirovat-${p.id}`}
                  onClick={() => kopiruj(plnaUrl(p.url_path), t("formKlient.poradce.zkopirovano"))}
                  className="inline-flex items-center gap-1.5 text-maly font-semibold py-2 px-3 rounded-full"
                  style={{ border: "1px solid var(--line)", color: "var(--ink)" }}>
                  <Copy size={13} aria-hidden="true" /> {t("formKlient.poradce.zkopirovat")}
                </button>
              )}
              {!p.zneplatneno && !p.odeslano_klientem && p.url_path && (
                <button type="button" onClick={() => zneplatni(p.id)} data-testid={`kf-zneplatnit-${p.id}`}
                  className="inline-flex items-center gap-1.5 text-maly py-2 px-3 rounded-full"
                  style={{ border: "1px solid var(--line)", color: "var(--muted)" }}>
                  <Ban size={13} aria-hidden="true" /> {t("formKlient.poradce.zneplatnit")}
                </button>
              )}
            </div>

            {(p.odeslano_klientem || p.vyplnenych_poli > 0) && (
              <Odpovedi polozka={p} jazyk={lang} t={t} />
            )}
          </article>
        );
      })}
    </section>
  );
}
